<script setup lang="ts">
const WINDOW_HEIGHT_MULTIPLIER = 0.9

type Props = {
  modelValue: boolean
  class?: string
  padding?: string | null
  persistentClose?: boolean
  scrollableElementSelector?: string
  additional?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  class: '',
  padding: null,
  scrollableElementSelector: undefined,
})

const bottomSheetContent = defineModel('bottomSheetContent', {
  required: false,
  default: true,
})

const emit = defineEmits(['update:modelValue', 'update:bottomSheetContent'])

const bottomSheetElement = ref<HTMLDivElement | null>(null)
const bottomSheetContentElement = ref<HTMLDivElement | null>(null)

const isVisibleCloseButton = ref(false)

const showCloseButton = computed(() => {
  return props.persistentClose || isVisibleCloseButton.value
})

const componentClasses = computed(() => {
  const classes = ['app-bottom-sheet']

  if (props.class) classes.push(props.class)

  return classes
})

const contentStyles = computed(() => {
  return props.padding ? { padding: `${props.padding}px` } : {}
})

const onClose = () => {
  emit('update:bottomSheetContent', true)
  emit('update:modelValue', false)
}

const resizeObserver = ref<ResizeObserver | null>(null)

const createResizeObserver = () => {
  const triggerHeight = window.innerHeight * WINDOW_HEIGHT_MULTIPLIER

  return new ResizeObserver((entries) => {
    if (!entries.length) return

    isVisibleCloseButton.value = entries[0].contentRect.height >= triggerHeight
  })
}

const getScrollableElement = () => {
  if (!props.scrollableElementSelector) return

  return bottomSheetContentElement.value?.querySelector(
    props.scrollableElementSelector,
  ) as HTMLElement
}

const checkScrollState = () => {
  const scrollableElement = getScrollableElement()

  if (scrollableElement) return scrollableElement.scrollTop === 0

  return bottomSheetContentElement.value?.scrollTop === 0
}

const setLockState = () => {
  if (props.additional) return

  scrollLock()
}

const setUnlockState = () => {
  if (props.additional) return

  scrollUnlock()
}

watch(bottomSheetElement, (element) => {
  if (element) resizeObserver.value?.observe(element)
})

watch(
  () => props.modelValue,
  (isActive) => {
    if (isActive) return setLockState()

    setUnlockState()
  },
)

const touchService = new TouchService({
  distance: 100,
  direction: 'down',
  onTouchEnd: () => checkScrollState() && onClose(),
})

onMounted(() => {
  resizeObserver.value = createResizeObserver()
})

onBeforeUnmount(() => {
  setUnlockState()

  resizeObserver.value?.disconnect()
})
</script>

<template>
  <Teleport to="body">
    <transition name="ui-fade">
      <template v-if="modelValue">
        <slot v-if="$slots.overlay" name="overlay" />

        <div
          v-else
          class="ui-dialog-overlay"
          data-test-id="bottom-sheet-overlay"
          @mousedown.self="onClose"
        />
      </template>
    </transition>

    <transition name="ui-slide-up">
      <div
        v-if="modelValue"
        v-show="bottomSheetContent"
        ref="bottomSheetElement"
        data-test-id="bottom-sheet"
        :class="componentClasses"
        role="dialog"
        aria-modal="true"
        @touchstart="touchService.onStart"
        @touchmove="touchService.onMove"
        @touchend="touchService.onEnd"
      >
        <div class="app-bottom-sheet__header" role="button" @click="onClose" />

        <the-button
          v-if="showCloseButton"
          data-test-id="bottom-sheet-close"
          class="app-bottom-sheet__button"
          variant="transparent"
          size="xs"
          radius="lg"
          icon="close-bigger"
          type="button"
          @click="onClose"
        />

        <div
          ref="bottomSheetContentElement"
          data-test-id="bottom-sheet-content"
          class="app-bottom-sheet__content"
          :style="contentStyles"
        >
          <slot />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-bottom-sheet {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9001;
  max-height: calc(100vh - 60px);
  overflow: hidden;
  background-color: var(--ui-white);
  border-radius: 20px 20px 0 0;
  will-change: transform;

  @supports (height: 100dvh) {
    max-height: calc(100dvh - 30px);
  }

  &__header {
    position: relative;
    height: 28px;
    color: var(--ui-text-secondary);

    &::before {
      position: absolute;
      top: 12px;
      left: 50%;
      width: 36px;
      height: 5px;
      content: '';
      background-color: var(--ui-secondary);
      border-radius: 10px;
      opacity: 0.2;
      transform: translateX(-50%);
    }
  }

  &__button {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1000;
    background-color: #fff;
    box-shadow: var(--ui-card-shadow);
  }

  &__content {
    position: relative;
    max-height: calc(100vh - 90px);
    padding: 13px 20px 24px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
    @include invisible-scroll-bar();

    @supports (height: 100dvh) {
      max-height: calc(100dvh - 60px);
    }
  }
}
</style>
